import * as THREE from 'three';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import { KitchenWorld } from '../assets/KitchenFactory';
import { GameAudio } from '../audio/GameAudio';
import { createRenderer, resizeRenderer } from '../core/Renderer';
import { GameUI } from '../ui/GameUI';
import { createSeededRandom } from '../utils/random';
import type {
  ChickenState,
  CustomerConversation,
  KitchenSnapshot,
  OrderState,
  OvenState,
  RiceState,
  SessionStats,
  StationId,
  UiIntent,
  VegetableState,
} from './types';

const ORDER_SECONDS = 90;
const MARINADE_SECONDS = 5;
const CHILL_SECONDS = 5;
const RICE_WASH_COOLDOWN = 2;
const VEGETABLE_CHOP_COOLDOWN = 1;
const OVEN_SECONDS = 10;
const DEFAULT_SESSION_SECONDS = 10 * 60;
const SESSION_DURATION_OPTIONS = [5, 10, 15, 20] as const;

const CUSTOMERS = ['أبو راكان', 'أم نورة', 'مشعل', 'سارة', 'عبدالله', 'نوف', 'فهد', 'دانة'];
const PRAISE = [
  'بيض الله وجهكم، وصل حار وسريع! 🔥',
  'هذا المندي اللي يفتح النفس، كفو!',
  'سرعة وطعم.. مندي كايف ما قصّر!',
  'الله يعطيكم العافية، ضبطتوا الطلب 👌',
];
const COMPLAINTS = [
  'أنا المخطي اللي أطلب من عندكم بهالزحمة.',
  'شغلكم طيب بس التأخير ما ينفع مع العملاء.',
  'الشرهة على اللي يطلب وهو مستعجل.',
  'يا جماعة الخير، الطلب له وقت والتأخير أتعبنا.',
];

export class KitchenGame {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(38, 1, 0.1, 80);
  private readonly world = new KitchenWorld();
  private readonly audio = new GameAudio();
  private readonly ui: GameUI;
  private readonly raycaster = new THREE.Raycaster();
  private readonly pointer = new THREE.Vector2();
  private readonly clock = new THREE.Clock();
  private readonly environment: THREE.Texture;

  private frameHandle = 0;
  private frame = 0;
  private elapsed = 0;
  private score = 0;
  private reputation = 100;
  private combo = 0;
  private completedOrders = 0;
  private paused = false;
  private muted = false;
  private tutorialVisible = true;
  private pausedForScreenshot = false;
  private reducedMotion = false;
  private transitionRemaining = 0;
  private toast: string | undefined;
  private toastRemaining = 0;
  private orderIndex = 0;
  private rng = createSeededRandom(42);
  private nextStation: StationId = 'chicken-crates';
  private attentionStations: StationId[] = [];
  private attentionRemaining = 0;
  private hint = 'ابدأ بكرتون الدجاج؛ خط الدجاج أطول من الباقي.';
  private lastPointerX = 0;
  private lastPointerY = 0;

  private conversations: CustomerConversation[] = [];
  private failedOrders = 0;
  private deliveryTimes: number[] = [];
  private sessionDuration = DEFAULT_SESSION_SECONDS;
  private configuredSessionDuration = DEFAULT_SESSION_SECONDS;
  private sessionRemaining = DEFAULT_SESSION_SECONDS;
  private sessionEnded = false;
  private sessionStats: SessionStats | undefined;
  private settingsVisible = false;
  private pausedBeforeSettings = false;

  private order: OrderState = this.createOrder(1);
  private chicken: ChickenState = this.createChickenState();
  private rice: RiceState = this.createRiceState();
  private vegetables: VegetableState = this.createVegetableState();
  private oven: OvenState = this.createOvenState();

  constructor(private readonly canvas: HTMLCanvasElement) {
    this.renderer = createRenderer(canvas);
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.toneMappingExposure = 0.84;
    const pmrem = new THREE.PMREMGenerator(this.renderer);
    this.environment = pmrem.fromScene(new RoomEnvironment(), 0.035).texture;
    pmrem.dispose();

    this.scene.background = new THREE.Color(0xf0dcc3);
    this.scene.environment = this.environment;
    this.scene.environmentIntensity = 0.82;
    this.scene.fog = new THREE.Fog(0xf0dcc3, 31, 48);
    this.scene.add(this.world.root);
    this.addLights();

    this.camera.position.set(0, 18.8, 16.5);
    this.camera.lookAt(0, 0.65, 0);
    resizeRenderer(this.renderer, this.camera, this.maxDpr());

    this.ui = new GameUI((intent) => this.handleIntent(intent));
    this.canvas.addEventListener('pointerdown', this.onPointerDown, { passive: false });
    this.canvas.addEventListener('pointermove', this.onPointerMove, { passive: true });
    window.addEventListener('resize', this.onResize);
    document.addEventListener('visibilitychange', this.onVisibilityChange);

    this.installTestHooks();
    this.computeGuidance();
    this.syncView(0);
    this.render();
    this.publishDiagnostics();
  }

  start(): void {
    this.clock.start();
    this.frameHandle = requestAnimationFrame(this.loop);
  }

  dispose(): void {
    cancelAnimationFrame(this.frameHandle);
    this.canvas.removeEventListener('pointerdown', this.onPointerDown);
    this.canvas.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.audio.dispose();
    this.ui.dispose();
    this.environment.dispose();
    this.renderer.dispose();
    window.__THREE_GAME_DIAGNOSTICS__ = undefined;
    window.__THREE_GAME_TEST_HOOKS__ = undefined;
  }

  private readonly loop = (): void => {
    const realDelta = Math.min(this.clock.getDelta(), 0.25);
    const animationDelta = Math.min(realDelta, 0.05);
    this.frame += 1;
    if (!this.pausedForScreenshot) this.update(realDelta);
    this.syncView(animationDelta);
    this.render();
    this.publishDiagnostics();
    this.frameHandle = requestAnimationFrame(this.loop);
  };

  private update(delta: number): void {
    if (this.paused || this.tutorialVisible) return;
    if (this.sessionEnded) return;
    this.elapsed += delta;

    this.sessionRemaining = Math.max(0, this.sessionRemaining - delta);
    if (this.sessionRemaining <= 0) {
      this.endSession();
      return;
    }

    if (this.toastRemaining > 0) {
      this.toastRemaining -= delta;
      if (this.toastRemaining <= 0) this.toast = undefined;
    }

    if (this.attentionRemaining > 0) {
      this.attentionRemaining = Math.max(0, this.attentionRemaining - delta);
      if (this.attentionRemaining === 0) this.attentionStations = [];
    }

    if (this.transitionRemaining > 0) {
      this.transitionRemaining -= delta;
      if (this.transitionRemaining <= 0) this.beginNextOrder();
      return;
    }

    if (this.order.status === 'active') {
      this.order.remaining = Math.max(0, this.order.remaining - delta);
      if (this.order.remaining <= 0) this.expireOrder();
    }

    this.tickTimer(
      () => this.chicken.marinadeRemaining,
      (value) => (this.chicken.marinadeRemaining = value),
      delta,
      'خلص التتبيل — انقل الدجاج للثلاجة.',
    );
    this.tickTimer(
      () => this.chicken.chillRemaining,
      (value) => (this.chicken.chillRemaining = value),
      delta,
      'اكتمل التبريد — أخرج الدجاج وجهّزه للفرن.',
    );
    if (this.rice.washCooldown > 0) this.rice.washCooldown = Math.max(0, this.rice.washCooldown - delta);
    if (this.vegetables.chopCooldown > 0) this.vegetables.chopCooldown = Math.max(0, this.vegetables.chopCooldown - delta);

    if (this.oven.cooking) {
      const before = this.oven.remaining;
      this.oven.remaining = Math.max(0, this.oven.remaining - delta);
      if (before > 0 && this.oven.remaining === 0) {
        this.oven.cooking = false;
        this.oven.platterReady = true;
        this.order.status = 'ready';
        this.updateConversation('ready', 'restaurant', 'صحنك صار جاهز وحار، باقي نسلمه للمندوب 🔥');
        this.audio.play('complete');
        this.showToast('جهز صحن المندي! سلّمه للمندوب.');
      }
    }

    this.computeGuidance();
  }

  private tickTimer(
    get: () => number,
    set: (value: number) => void,
    delta: number,
    readyMessage: string,
  ): void {
    const before = get();
    if (before <= 0) return;
    const after = Math.max(0, before - delta);
    set(after);
    if (before > 0 && after === 0) {
      this.audio.play('timer-ready');
      this.showToast(readyMessage);
    }
  }

  private syncView(delta: number): void {
    const snapshot = this.getSnapshot();
    this.world.update(snapshot, delta, this.elapsed, this.reducedMotion);
    this.ui.render(snapshot);

    if (!this.reducedMotion && !this.pausedForScreenshot) {
      const aspect = Math.max(0.45, this.canvas.clientWidth / Math.max(1, this.canvas.clientHeight));
      const mobile = aspect < 0.78;
      const targetX = this.lastPointerX * (mobile ? 0.1 : 0.42);
      const targetY = 0.65 + this.lastPointerY * 0.08;
      const ease = 1 - Math.exp(-delta * 1.8);
      this.camera.position.x = THREE.MathUtils.lerp(this.camera.position.x, targetX, ease);
      this.camera.position.y = THREE.MathUtils.lerp(this.camera.position.y, mobile ? 21.5 : 18.8, ease);
      this.camera.position.z = THREE.MathUtils.lerp(this.camera.position.z, mobile ? 23 : 16.5, ease);
      this.camera.lookAt(targetX * 0.12, targetY, 0);
    }
  }

  private render(): void {
    this.renderer.render(this.scene, this.camera);
  }

  private addLights(): void {
    const hemi = new THREE.HemisphereLight(0xfff2df, 0x80654f, 0.94);
    this.scene.add(hemi);

    const key = new THREE.DirectionalLight(0xffe5bf, 1.9);
    key.position.set(-7, 12, 9);
    key.castShadow = true;
    key.shadow.mapSize.set(2048, 2048);
    key.shadow.camera.left = -13;
    key.shadow.camera.right = 13;
    key.shadow.camera.top = 11;
    key.shadow.camera.bottom = -11;
    key.shadow.camera.near = 1;
    key.shadow.camera.far = 36;
    key.shadow.bias = -0.0008;
    this.scene.add(key);

    const rim = new THREE.DirectionalLight(0x6ee8e0, 0.72);
    rim.position.set(8, 8, -8);
    this.scene.add(rim);

    const warm = new THREE.PointLight(0xff6b32, 3.2, 8, 2);
    warm.position.set(5.4, 2.7, 0.8);
    this.scene.add(warm);
  }

  private readonly onPointerMove = (event: PointerEvent): void => {
    const rect = this.canvas.getBoundingClientRect();
    this.lastPointerX = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.lastPointerY = -(((event.clientY - rect.top) / rect.height) * 2 - 1);
    const station = this.pickStation(event.clientX, event.clientY);
    this.canvas.style.cursor = station ? 'pointer' : 'default';
  };

  private readonly onPointerDown = (event: PointerEvent): void => {
    event.preventDefault();
    void this.audio.unlock();
    if (this.tutorialVisible || this.paused || this.sessionEnded || this.transitionRemaining > 0) return;
    const station = this.pickStation(event.clientX, event.clientY);
    if (station) this.handleStation(station);
  };

  private pickStation(clientX: number, clientY: number): StationId | null {
    const rect = this.canvas.getBoundingClientRect();
    this.pointer.x = ((clientX - rect.left) / rect.width) * 2 - 1;
    this.pointer.y = -((clientY - rect.top) / rect.height) * 2 + 1;
    this.raycaster.setFromCamera(this.pointer, this.camera);
    const hits = this.raycaster.intersectObjects(this.world.interactables, false);
    for (const hit of hits) {
      const stationId = hit.object.userData.stationId as StationId | undefined;
      if (stationId) return stationId;
    }
    return null;
  }

  private handleIntent(intent: UiIntent): void {
    void this.audio.unlock();
    switch (intent.type) {
      case 'station':
        if (!this.tutorialVisible && !this.paused && !this.sessionEnded && this.transitionRemaining <= 0) this.handleStation(intent.station);
        break;
      case 'pause-toggle':
        if (!this.sessionEnded && !this.settingsVisible) {
          this.paused = !this.paused;
          this.audio.play('ui');
        }
        break;
      case 'mute-toggle':
        this.muted = this.audio.toggleMuted();
        break;
      case 'settings-show':
        this.openSettings();
        break;
      case 'settings-hide':
        this.closeSettings();
        break;
      case 'set-session-duration': {
        if (SESSION_DURATION_OPTIONS.includes(intent.minutes as (typeof SESSION_DURATION_OPTIONS)[number])) {
          this.configuredSessionDuration = intent.minutes * 60;
          this.audio.play('ui');
        }
        break;
      }
      case 'new-session':
        this.startNewSession();
        break;
      case 'restart':
        if (this.sessionEnded) break;
        this.audio.play('ui');
        this.resetKitchen();
        this.order = this.createOrder(this.order.chickens);
        this.computeGuidance();
        break;
      case 'tutorial-dismiss':
        this.tutorialVisible = false;
        this.audio.play('ui');
        this.showToast('ابدأ بكرتون الدجاج، واشتغل على الرز والخضار أثناء الانتظار.');
        break;
      case 'tutorial-show':
        this.tutorialVisible = true;
        this.audio.play('ui');
        break;
    }
  }

  private handleStation(station: StationId): void {
    this.world.pulseStation(station);
    let changed = false;
    switch (station) {
      case 'chicken-crates':
        changed = this.useChickenCrates();
        break;
      case 'chicken-board':
        changed = this.useChickenBoard();
        break;
      case 'marinade':
        changed = this.useMarinade();
        break;
      case 'fridge':
        changed = this.useFridge();
        break;
      case 'rice-bags':
        changed = this.useRiceBags();
        break;
      case 'sink':
        changed = this.useSink();
        break;
      case 'veg-basket':
        changed = this.useVegetables();
        break;
      case 'veg-board':
        changed = this.useVegetableBoard();
        break;
      case 'oven':
        changed = this.useOven();
        break;
      case 'serve':
        changed = this.useCourier();
        break;
    }
    if (changed) {
      this.clearAttention();
      this.computeGuidance();
      this.score += 5;
    }
  }

  private useChickenCrates(): boolean {
    const inPipeline =
      this.chicken.cratesTaken +
      this.chicken.onBoard +
      this.chicken.cutHalves / 2 +
      this.chicken.marinadeCount +
      this.chicken.chilledCount +
      this.chicken.ovenReady;
    if (inPipeline >= this.order.chickens) {
      this.showToast('جهزت عدد الدجاج المطلوب لهذا الطلب.');
      return false;
    }
    if (this.chicken.cratesTaken || this.chicken.onBoard || this.chicken.cutHalves) {
      this.showToast('خلّص الدجاج الموجود على طاولة التقطيع أولًا.');
      return false;
    }
    this.chicken.cratesTaken = 1;
    this.audio.play('pickup');
    this.showToast('وصل الكرتون للقطاعة — افتحه الآن.');
    return true;
  }

  private useChickenBoard(): boolean {
    if (this.chicken.cratesTaken > 0) {
      this.chicken.cratesTaken = 0;
      this.chicken.onBoard = 1;
      this.audio.play('pickup');
      this.showToast('طلعت الدجاجة — اضغط مرة ثانية لتقسيمها نصفين.');
      return true;
    }
    if (this.chicken.onBoard > 0) {
      this.chicken.onBoard = 0;
      this.chicken.cutHalves = 2;
      this.audio.play('chop');
      this.showToast('تقطعت الدجاجة نصفين — حطها في التتبيلة.');
      return true;
    }
    this.showToast('هات كرتون دجاج أولًا.');
    this.pointToRequirements(['chicken-crates']);
    return false;
  }

  private useMarinade(): boolean {
    if (this.chicken.marinadeCount > 0) {
      const seconds = Math.ceil(this.chicken.marinadeRemaining);
      if (seconds <= 0) this.pointToRequirements(['fridge']);
      this.showToast(seconds > 0 ? `التتبيل يحتاج ${seconds} ث.` : 'التتبيل جاهز — انقله للثلاجة.');
      return false;
    }
    if (this.chicken.cutHalves < 2) {
      this.pointToRequirements([this.getChickenRequirementStation()]);
      this.showToast('نحتاج دجاجة مقطوعة نصفين قبل التتبيل.');
      return false;
    }
    this.chicken.cutHalves -= 2;
    this.chicken.marinadeCount = 1;
    this.chicken.marinadeRemaining = MARINADE_SECONDS;
    this.audio.play('splash');
    this.showToast('الدجاج في الخلطة — انتظر 5 ثوانٍ.');
    return true;
  }

  private useFridge(): boolean {
    if (this.chicken.chilledCount > 0) {
      if (this.chicken.chillRemaining > 0) {
        this.showToast(`التبريد يحتاج ${Math.ceil(this.chicken.chillRemaining)} ث.`);
        return false;
      }
      this.chicken.chilledCount = 0;
      this.chicken.ovenReady += 1;
      this.audio.play('fridge');
      this.showToast(`دجاج جاهز للفرن (${this.chicken.ovenReady}/${this.order.chickens}).`);
      return true;
    }
    if (this.chicken.marinadeCount <= 0 || this.chicken.marinadeRemaining > 0) {
      this.pointToRequirements([this.getChickenRequirementStation()]);
      this.showToast(this.chicken.marinadeCount ? 'انتظر اكتمال التتبيل.' : 'ما فيه دجاج متبل للثلاجة.');
      return false;
    }
    this.chicken.marinadeCount = 0;
    this.chicken.chilledCount = 1;
    this.chicken.chillRemaining = CHILL_SECONDS;
    this.audio.play('fridge');
    this.showToast('دخل الدجاج الثلاجة — انتظر 5 ثوانٍ.');
    return true;
  }

  private useRiceBags(): boolean {
    if (!this.rice.bagPlaced) {
      this.rice.bagPlaced = true;
      this.audio.play('pickup');
      this.showToast('وصل كيس الرز بجانب المغسلة. اضغط الكيس مرة ثانية.');
      return true;
    }
    if (!this.rice.bowlFilled) {
      this.rice.bowlFilled = true;
      this.audio.play('pour');
      this.showToast('تعبت البادية رز — اغسلها 3 مرات.');
      return true;
    }
    this.showToast('الرز موجود في البادية عند المغسلة.');
    this.pointToRequirements(['sink']);
    return false;
  }

  private useSink(): boolean {
    if (!this.rice.bowlFilled) {
      this.pointToRequirements(['rice-bags']);
      this.showToast('حط كمية رز في البادية أولًا.');
      return false;
    }
    if (this.rice.ovenReady) {
      this.pointToRequirements(this.getOvenRequirementStations());
      this.showToast('الرز جاهز داخل قدر الفرن.');
      return false;
    }
    if (this.rice.washCooldown > 0) {
      this.showToast(`انتظر ${Math.ceil(this.rice.washCooldown)} ث. بين الغسلات.`);
      return false;
    }
    if (this.rice.washCount < 3) {
      this.rice.washCount += 1;
      this.rice.washCooldown = RICE_WASH_COOLDOWN;
      this.audio.play('splash');
      this.showToast(`غسلة ${this.rice.washCount}/3 — صفّ الماء وانتظر ثانيتين.`);
      return true;
    }
    this.rice.ovenReady = true;
    this.audio.play('pour');
    this.showToast('انتقل الرز إلى القدر الكبير داخل الفرن.');
    return true;
  }

  private useVegetables(): boolean {
    if (!this.vegetables.placed) {
      this.vegetables.placed = true;
      this.audio.play('pickup');
      this.showToast('وصل صندوق الخضار للقطاعة — اضغطه مرة ثانية.');
      return true;
    }
    if (!this.vegetables.revealed) {
      this.vegetables.revealed = true;
      this.audio.play('pickup');
      this.showToast('بصل وبطاطس وطماط جاهزة — تحتاج 3 ضغطات تقطيع.');
      return true;
    }
    this.showToast('الخضار على لوح التقطيع.');
    this.pointToRequirements(['veg-board']);
    return false;
  }

  private useVegetableBoard(): boolean {
    if (!this.vegetables.revealed) {
      this.pointToRequirements(['veg-basket']);
      this.showToast('انقل الخضار وافتح الصندوق أولًا.');
      return false;
    }
    if (this.vegetables.ovenReady) {
      this.pointToRequirements(this.getOvenRequirementStations());
      this.showToast('الخضار جاهزة في رف الفرن.');
      return false;
    }
    if (this.vegetables.chopCooldown > 0) {
      this.showToast('تمهّل ثانية بين كل تقطيعة.');
      return false;
    }
    if (this.vegetables.chopCount < 3) {
      this.vegetables.chopCount += 1;
      this.vegetables.chopCooldown = VEGETABLE_CHOP_COOLDOWN;
      this.audio.play('chop');
      this.showToast(`تقطيع ${this.vegetables.chopCount}/3 — البطاطس شرائح والباقي مكعبات.`);
      return true;
    }
    this.vegetables.ovenReady = true;
    this.audio.play('pour');
    this.showToast('انتقلت الخضار إلى رف فرن المندي.');
    return true;
  }

  private useOven(): boolean {
    if (this.oven.platterReady) {
      this.pointToRequirements(['serve']);
      this.showToast('الصحن جاهز — اضغط المندوب لتسليمه.');
      return false;
    }
    if (this.oven.cooking) {
      this.showToast(`الطبخ يحتاج ${Math.ceil(this.oven.remaining)} ث.`);
      return false;
    }
    if (this.chicken.ovenReady < this.order.chickens || !this.rice.ovenReady || !this.vegetables.ovenReady) {
      const missing = [
        this.chicken.ovenReady < this.order.chickens ? `الدجاج ${this.chicken.ovenReady}/${this.order.chickens}` : '',
        !this.rice.ovenReady ? 'الرز' : '',
        !this.vegetables.ovenReady ? 'الخضار' : '',
      ].filter(Boolean);
      this.pointToRequirements(this.getOvenRequirementStations());
      this.showToast(`ناقص للفرن: ${missing.join('، ')}.`);
      return false;
    }
    this.oven.loaded = true;
    this.oven.cooking = true;
    this.oven.remaining = OVEN_SECONDS;
    this.audio.play('oven');
    this.showToast('قفلنا فرن المندي — الطبخ يحتاج 10 ثوانٍ.');
    return true;
  }

  private useCourier(): boolean {
    if (!this.oven.platterReady || this.order.status !== 'ready') {
      this.pointToRequirements(this.getOvenRequirementStations());
      this.showToast('المندوب ينتظر صحنًا جاهزًا.');
      return false;
    }
    const fast = this.order.remaining > this.order.total / 2;
    const bonus = Math.round(this.order.remaining * (fast ? 3 : 1.5));
    this.score += 250 + bonus + this.combo * 35;
    this.reputation = Math.min(100, this.reputation + (fast ? 4 : 2));
    this.combo += 1;
    this.completedOrders += 1;
    const deliverySeconds = Math.max(0, this.order.total - this.order.remaining);
    this.deliveryTimes.push(deliverySeconds);
    this.order.status = 'delivered';
    this.order.reaction = PRAISE[Math.floor(this.rng() * PRAISE.length) % PRAISE.length];
    this.updateConversation(
      'delivered',
      'customer',
      this.order.reaction,
      fast ? 5 : 4,
      deliverySeconds,
    );
    this.oven.platterReady = false;
    this.transitionRemaining = 4;
    this.audio.play('deliver');
    this.showToast(fast ? 'تسليم برق! كسبت مكافأة سرعة.' : 'تم التسليم بنجاح.');
    return true;
  }

  private expireOrder(): void {
    this.order.status = 'expired';
    this.order.reaction = COMPLAINTS[Math.floor(this.rng() * COMPLAINTS.length) % COMPLAINTS.length];
    this.failedOrders += 1;
    this.updateConversation('expired', 'customer', this.order.reaction, 1, this.order.total);
    this.reputation = Math.max(0, this.reputation - 10);
    this.combo = 0;
    this.transitionRemaining = 5;
    this.audio.play('fail');
    this.showToast('انتهى وقت الطلب — بنجهز طلبًا جديدًا بسرعة.');
  }

  private beginNextOrder(): void {
    if (this.sessionEnded || this.sessionRemaining <= 0) return;
    this.resetKitchen();
    const chickens = 1 + Math.floor(this.rng() * 3);
    this.order = this.createOrder(chickens);
    this.computeGuidance();
    this.showToast(`طلب جديد: صحن فيه ${this.chickenLabel(chickens)}.`);
  }

  private resetKitchen(): void {
    this.chicken = this.createChickenState();
    this.rice = this.createRiceState();
    this.vegetables = this.createVegetableState();
    this.oven = this.createOvenState();
    this.transitionRemaining = 0;
    this.clearAttention();
  }

  private createOrder(chickens: number): OrderState {
    this.orderIndex += 1;
    const customer = CUSTOMERS[Math.floor(this.rng() * CUSTOMERS.length) % CUSTOMERS.length];
    const order: OrderState = {
      id: this.orderIndex,
      customer,
      chickens,
      remaining: ORDER_SECONDS,
      total: ORDER_SECONDS,
      status: 'active',
    };
    const sentAt = this.sessionElapsedSeconds();
    this.conversations.unshift({
      orderId: order.id,
      customer,
      chickens,
      status: 'active',
      messages: [
        {
          id: `${order.id}-customer-order`,
          sender: 'customer',
          text: this.orderRequestMessage(chickens),
          sentAt,
        },
        {
          id: `${order.id}-restaurant-ack`,
          sender: 'restaurant',
          text: 'أبشر، طلبك وصل وبدأنا التجهيز 👌',
          sentAt: sentAt + 0.01,
        },
      ],
    });
    return order;
  }

  private orderRequestMessage(chickens: number): string {
    return `السلام عليكم، نبي صحن مندي فيه ${this.chickenLabel(chickens)} لو سمحت.`;
  }

  private sessionElapsedSeconds(): number {
    return Math.max(0, this.sessionDuration - this.sessionRemaining);
  }

  private updateConversation(
    status: OrderState['status'],
    sender?: 'customer' | 'restaurant',
    text?: string,
    rating?: number,
    deliverySeconds?: number,
  ): void {
    const conversation = this.conversations.find((item) => item.orderId === this.order.id);
    if (!conversation) return;
    conversation.status = status;
    if (rating !== undefined) conversation.rating = rating;
    if (deliverySeconds !== undefined) conversation.deliverySeconds = deliverySeconds;
    if (sender && text && conversation.messages.at(-1)?.text !== text) {
      conversation.messages.push({
        id: `${conversation.orderId}-${sender}-${conversation.messages.length + 1}`,
        sender,
        text,
        sentAt: this.sessionElapsedSeconds(),
      });
    }
  }

  private openSettings(): void {
    if (this.settingsVisible) return;
    this.pausedBeforeSettings = this.paused;
    this.settingsVisible = true;
    this.paused = true;
    this.audio.play('ui');
  }

  private closeSettings(): void {
    if (!this.settingsVisible) return;
    this.settingsVisible = false;
    this.paused = this.sessionEnded ? true : this.pausedBeforeSettings;
    this.audio.play('ui');
  }

  private startNewSession(): void {
    this.audio.play('ui');
    this.score = 0;
    this.reputation = 100;
    this.combo = 0;
    this.completedOrders = 0;
    this.failedOrders = 0;
    this.deliveryTimes = [];
    this.conversations = [];
    this.orderIndex = 0;
    this.sessionDuration = this.configuredSessionDuration;
    this.sessionRemaining = this.sessionDuration;
    this.sessionEnded = false;
    this.sessionStats = undefined;
    this.settingsVisible = false;
    this.pausedBeforeSettings = false;
    this.tutorialVisible = false;
    this.paused = false;
    this.resetKitchen();
    this.order = this.createOrder(1);
    this.computeGuidance();
    this.showToast(`بدأت وردية جديدة مدتها ${Math.round(this.sessionDuration / 60)} دقائق.`);
  }

  private endSession(): void {
    if (this.sessionEnded) return;
    if (this.order.status === 'active' || this.order.status === 'ready') {
      this.order.status = 'expired';
      this.order.reaction = 'انتهت الوردية قبل ما يوصل طلبي، إن شاء الله المرة الجاية أسرع.';
      this.failedOrders += 1;
      this.updateConversation('expired', 'customer', this.order.reaction, 2, this.order.total - this.order.remaining);
    }
    this.sessionRemaining = 0;
    this.sessionEnded = true;
    this.sessionStats = this.buildSessionStats();
    this.settingsVisible = false;
    this.transitionRemaining = 0;
    this.paused = true;
    this.audio.play('complete');
  }

  private buildSessionStats(): SessionStats {
    const totalDeliverySeconds = this.deliveryTimes.reduce((sum, value) => sum + value, 0);
    const averageDeliverySeconds = this.deliveryTimes.length > 0
      ? totalDeliverySeconds / this.deliveryTimes.length
      : 0;
    return {
      totalOrders: this.completedOrders + this.failedOrders,
      deliveredOrders: this.completedOrders,
      expiredOrders: this.failedOrders,
      averageDeliverySeconds,
      bestDeliverySeconds: this.deliveryTimes.length > 0 ? Math.min(...this.deliveryTimes) : undefined,
      positiveReviews: this.conversations.filter((item) => (item.rating ?? 0) >= 4).length,
      negativeReviews: this.conversations.filter((item) => item.rating !== undefined && item.rating <= 2).length,
      finalScore: this.score,
      finalReputation: this.reputation,
    };
  }

  private createChickenState(): ChickenState {
    return {
      cratesTaken: 0,
      onBoard: 0,
      cutHalves: 0,
      marinadeCount: 0,
      marinadeRemaining: 0,
      chilledCount: 0,
      chillRemaining: 0,
      ovenReady: 0,
    };
  }

  private createRiceState(): RiceState {
    return { bagPlaced: false, bowlFilled: false, washCount: 0, washCooldown: 0, ovenReady: false };
  }

  private createVegetableState(): VegetableState {
    return { placed: false, revealed: false, chopCount: 0, chopCooldown: 0, ovenReady: false };
  }

  private createOvenState(): OvenState {
    return { loaded: false, cooking: false, remaining: 0, platterReady: false };
  }

  private computeGuidance(): void {
    if (this.oven.platterReady) {
      this.nextStation = 'serve';
      this.hint = 'الصحن جاهز — سلّمه للمندوب قبل ما يبرد.';
      return;
    }
    if (this.oven.cooking) {
      this.nextStation = 'oven';
      this.hint = `المندي يطبخ… ${Math.ceil(this.oven.remaining)} ث.`;
      return;
    }

    const chickenAction = this.getChickenAction();
    const riceAction = this.getRiceAction();
    const vegAction = this.getVegetableAction();

    if (chickenAction) {
      [this.nextStation, this.hint] = chickenAction;
      return;
    }
    if (riceAction) {
      [this.nextStation, this.hint] = riceAction;
      return;
    }
    if (vegAction) {
      [this.nextStation, this.hint] = vegAction;
      return;
    }
    if (this.chicken.ovenReady >= this.order.chickens && this.rice.ovenReady && this.vegetables.ovenReady) {
      this.nextStation = 'oven';
      this.hint = 'كل شيء جاهز — حمّل فرن المندي.';
      return;
    }
    this.nextStation = 'oven';
    this.hint = 'استغل وقت الانتظار وجهّز خطًا آخر.';
  }

  private getChickenAction(): [StationId, string] | null {
    if (this.chicken.ovenReady >= this.order.chickens) return null;
    if (this.chicken.chilledCount > 0) {
      return this.chicken.chillRemaining > 0
        ? null
        : ['fridge', 'أخرج الدجاج المبرد وجهّزه للفرن.'];
    }
    if (this.chicken.marinadeCount > 0) {
      return this.chicken.marinadeRemaining > 0
        ? null
        : ['fridge', 'انقل الدجاج المتبل إلى الثلاجة.'];
    }
    if (this.chicken.cutHalves >= 2) return ['marinade', 'غمّس نصفي الدجاج في الخلطة.'];
    if (this.chicken.cratesTaken || this.chicken.onBoard) return ['chicken-board', 'افتح الكرتون ثم اقطع الدجاجة نصفين.'];
    return ['chicken-crates', `جهّز دجاجة ${this.chicken.ovenReady + 1} من ${this.order.chickens}.`];
  }

  private getRiceAction(): [StationId, string] | null {
    if (this.rice.ovenReady) return null;
    if (!this.rice.bagPlaced || !this.rice.bowlFilled) return ['rice-bags', 'حط كيس الرز بجانب المغسلة ثم عبّ البادية.'];
    if (this.rice.washCount < 3) {
      return this.rice.washCooldown > 0 ? null : ['sink', `اغسل الرز (${this.rice.washCount}/3).`];
    }
    return this.rice.washCooldown > 0 ? null : ['sink', 'انقل الرز المغسول إلى قدر الفرن.'];
  }

  private getVegetableAction(): [StationId, string] | null {
    if (this.vegetables.ovenReady) return null;
    if (!this.vegetables.placed || !this.vegetables.revealed) return ['veg-basket', 'انقل الخضار للقطاعة وافتح الصندوق.'];
    if (this.vegetables.chopCount < 3) {
      return this.vegetables.chopCooldown > 0 ? null : ['veg-board', `قطّع الخضار (${this.vegetables.chopCount}/3).`];
    }
    return this.vegetables.chopCooldown > 0 ? null : ['veg-board', 'انقل الخضار المقطعة إلى رف الفرن.'];
  }

  private getChickenRequirementStation(): StationId {
    if (this.chicken.chilledCount > 0) return 'fridge';
    if (this.chicken.marinadeCount > 0) return this.chicken.marinadeRemaining > 0 ? 'marinade' : 'fridge';
    if (this.chicken.cutHalves >= 2) return 'marinade';
    if (this.chicken.cratesTaken > 0 || this.chicken.onBoard > 0) return 'chicken-board';
    return 'chicken-crates';
  }

  private getRiceRequirementStation(): StationId {
    if (!this.rice.bagPlaced || !this.rice.bowlFilled) return 'rice-bags';
    return 'sink';
  }

  private getVegetableRequirementStation(): StationId {
    if (!this.vegetables.placed || !this.vegetables.revealed) return 'veg-basket';
    return 'veg-board';
  }

  private getOvenRequirementStations(): StationId[] {
    const required: StationId[] = [];
    if (this.chicken.ovenReady < this.order.chickens) required.push(this.getChickenRequirementStation());
    if (!this.rice.ovenReady) required.push(this.getRiceRequirementStation());
    if (!this.vegetables.ovenReady) required.push(this.getVegetableRequirementStation());
    return required.length > 0 ? [...new Set(required)] : ['oven'];
  }

  private pointToRequirements(stations: StationId[]): void {
    this.attentionStations = [...new Set(stations)];
    this.attentionRemaining = 3.6;
  }

  private clearAttention(): void {
    this.attentionStations = [];
    this.attentionRemaining = 0;
  }

  private showToast(message: string): void {
    this.toast = message;
    this.toastRemaining = 2.7;
    this.ui.flash(message);
  }

  private getSnapshot(): KitchenSnapshot {
    return {
      order: { ...this.order },
      chicken: { ...this.chicken },
      rice: { ...this.rice },
      vegetables: { ...this.vegetables },
      oven: { ...this.oven },
      score: this.score,
      reputation: this.reputation,
      combo: this.combo,
      completedOrders: this.completedOrders,
      conversations: this.conversations.map((conversation) => ({
        ...conversation,
        messages: conversation.messages.map((message) => ({ ...message })),
      })),
      session: {
        duration: this.sessionDuration,
        remaining: this.sessionRemaining,
        configuredDuration: this.configuredSessionDuration,
        ended: this.sessionEnded,
        settingsVisible: this.settingsVisible,
        stats: this.sessionStats ? { ...this.sessionStats } : undefined,
      },
      paused: this.paused,
      muted: this.muted,
      tutorialVisible: this.tutorialVisible,
      nextStation: this.nextStation,
      attentionStations: [...this.attentionStations],
      hint: this.hint,
      toast: this.toast,
    };
  }

  private chickenLabel(count: number): string {
    if (count === 1) return 'دجاجة واحدة';
    if (count === 2) return 'دجاجتين';
    return '3 دجاجات';
  }

  private readonly onResize = (): void => {
    resizeRenderer(this.renderer, this.camera, this.maxDpr());
    const aspect = this.canvas.clientWidth / Math.max(1, this.canvas.clientHeight);
    this.camera.fov = aspect < 0.78 ? 52 : aspect < 1.15 ? 43 : 38;
    this.camera.updateProjectionMatrix();
  };

  private readonly onVisibilityChange = (): void => {
    if (document.hidden && !this.paused) this.paused = true;
  };

  private maxDpr(): number {
    return window.matchMedia('(pointer: coarse)').matches ? 1.5 : 2;
  }

  private installTestHooks(): void {
    window.__THREE_GAME_TEST_HOOKS__ = {
      seed: (value: number) => {
        this.rng = createSeededRandom(value);
      },
      setState: async (name: string) => {
        this.tutorialVisible = false;
        this.paused = false;
        this.settingsVisible = false;
        this.sessionEnded = false;
        this.sessionStats = undefined;
        this.sessionDuration = DEFAULT_SESSION_SECONDS;
        this.configuredSessionDuration = DEFAULT_SESSION_SECONDS;
        this.sessionRemaining = DEFAULT_SESSION_SECONDS;
        this.score = 0;
        this.reputation = 100;
        this.combo = 0;
        this.completedOrders = 0;
        this.failedOrders = 0;
        this.deliveryTimes = [];
        this.conversations = [];
        this.orderIndex = 0;
        this.transitionRemaining = 0;
        this.clearAttention();
        this.resetKitchen();
        this.order = this.createOrder(name === 'active-play' ? 2 : 1);
        if (name === 'mid-prep') {
          this.chicken.marinadeCount = 1;
          this.chicken.marinadeRemaining = 2.4;
          this.rice.bagPlaced = true;
          this.rice.bowlFilled = true;
          this.rice.washCount = 2;
          this.vegetables.placed = true;
          this.vegetables.revealed = true;
          this.vegetables.chopCount = 2;
        } else if (name === 'oven-cooking') {
          this.chicken.ovenReady = 1;
          this.rice.ovenReady = true;
          this.vegetables.ovenReady = true;
          this.oven.loaded = true;
          this.oven.cooking = true;
          this.oven.remaining = 6.4;
        } else if (name === 'platter-ready') {
          this.chicken.ovenReady = 1;
          this.rice.ovenReady = true;
          this.vegetables.ovenReady = true;
          this.oven.loaded = true;
          this.oven.platterReady = true;
          this.order.status = 'ready';
        } else if (name === 'expired') {
          this.order.remaining = 0;
          this.order.status = 'expired';
          this.order.reaction = COMPLAINTS[0];
          this.failedOrders = 1;
          this.updateConversation('expired', 'customer', this.order.reaction, 1, this.order.total);
        } else if (name === 'settings-open') {
          this.settingsVisible = true;
          this.paused = true;
        } else if (name === 'session-ended') {
          this.score = 1480;
          this.reputation = 92;
          this.completedOrders = 2;
          this.failedOrders = 1;
          this.deliveryTimes = [38, 57];
          this.order.status = 'expired';
          this.order.reaction = COMPLAINTS[1];
          this.updateConversation('expired', 'customer', this.order.reaction, 1, 90);
          const current = this.conversations[0];
          this.conversations = [
            {
              orderId: 3,
              customer: 'سارة',
              chickens: 1,
              status: 'delivered',
              rating: 5,
              deliverySeconds: 38,
              messages: [
                { id: '3-a', sender: 'customer', text: 'نبي صحن مندي بدجاجة واحدة لو سمحت.', sentAt: 25 },
                { id: '3-b', sender: 'restaurant', text: 'أبشري، تحت التجهيز 👌', sentAt: 26 },
                { id: '3-c', sender: 'customer', text: PRAISE[0], sentAt: 63 },
              ],
            },
            {
              orderId: 2,
              customer: 'أبو راكان',
              chickens: 2,
              status: 'delivered',
              rating: 4,
              deliverySeconds: 57,
              messages: [
                { id: '2-a', sender: 'customer', text: 'عليكم بصحن فيه دجاجتين.', sentAt: 82 },
                { id: '2-b', sender: 'customer', text: PRAISE[2], sentAt: 139 },
              ],
            },
            current,
          ];
          this.sessionRemaining = 0;
          this.sessionEnded = true;
          this.paused = true;
          this.sessionStats = this.buildSessionStats();
        } else if (name === 'attention-guidance') {
          this.pointToRequirements(['chicken-crates', 'rice-bags', 'veg-basket']);
        } else if (name !== 'active-play') {
          throw new Error(`Unknown test state: ${name}`);
        }
        this.computeGuidance();
        this.syncView(0);
        this.render();
        this.publishDiagnostics();
        return { state: name };
      },
      setPausedForScreenshot: (paused: boolean) => {
        this.pausedForScreenshot = paused;
      },
      setReducedMotion: (enabled: boolean) => {
        this.reducedMotion = enabled;
        this.syncView(0);
        this.render();
      },
      hideDebugUi: () => undefined,
    };
  }

  private publishDiagnostics(): void {
    const info = this.renderer.info;
    window.__THREE_GAME_DIAGNOSTICS__ = {
      frame: this.frame,
      elapsed: this.elapsed,
      score: this.score,
      targetScore: this.order.chickens,
      complete: this.oven.platterReady || this.order.status === 'delivered',
      player: {
        position: { x: this.world.stations.get(this.nextStation)?.position.x ?? 0, y: 0, z: this.world.stations.get(this.nextStation)?.position.z ?? 0 },
        speed: 0,
      },
      renderer: {
        calls: info.render.calls,
        triangles: info.render.triangles,
        geometries: info.memory.geometries,
        textures: info.memory.textures,
      },
      canvas: {
        clientWidth: this.canvas.clientWidth,
        clientHeight: this.canvas.clientHeight,
        width: this.canvas.width,
        height: this.canvas.height,
        dpr: Math.min(window.devicePixelRatio || 1, this.maxDpr()),
      },
      state: {
        orderStatus: this.order.status,
        chickenOvenReady: this.chicken.ovenReady,
        chickenMarinadeRemaining: this.chicken.marinadeRemaining,
        chickenChillRemaining: this.chicken.chillRemaining,
        riceWashCount: this.rice.washCount,
        riceWashCooldown: this.rice.washCooldown,
        riceOvenReady: this.rice.ovenReady,
        vegetableChopCount: this.vegetables.chopCount,
        vegetableChopCooldown: this.vegetables.chopCooldown,
        vegetablesOvenReady: this.vegetables.ovenReady,
        ovenCooking: this.oven.cooking,
        ovenRemaining: this.oven.remaining,
        platterReady: this.oven.platterReady,
        nextStation: this.nextStation,
        sessionRemaining: this.sessionRemaining,
        sessionEnded: this.sessionEnded,
        settingsVisible: this.settingsVisible,
        conversationCount: this.conversations.length,
        attentionStations: [...this.attentionStations],
      },
    };
  }
}

